import { Node as PmNode, Mark as PmMark } from "prosemirror-model";
import type {
  Block,
  Document,
  Inline,
  ListItem,
  ListStyle,
  Mark,
} from "../types";
import { schema } from "./schema";

// Card content travels as a domain AST (see types.ts), not Markdown. These
// functions translate between that AST and the ProseMirror document the
// editor operates on. The AST mirrors the schema 1:1. Content that does not
// agree with the schema causes an error, because an editor that shows less
// than the card would save the smaller content over the card.

// ── ProseMirror doc → Document AST ────────────────────────────────────────────

export function serialize(doc: PmNode): Document {
  const blocks: Block[] = [];
  doc.forEach((node) => blocks.push(blockFromPm(node)));
  return { blocks };
}

function blockFromPm(node: PmNode): Block {
  switch (node.type.name) {
    case "paragraph":
      return { kind: "paragraph", content: inlineFromPm(node) };
    case "heading":
      return { kind: "heading", level: node.attrs["level"] as number, content: inlineFromPm(node) };
    case "code_block":
      return { kind: "code_block", text: node.textContent };
    case "list": {
      const style = node.attrs["style"] as ListStyle;
      const base = { kind: "list" as const, style, items: listItemsFromPm(node) };
      return style === "ordered" ? { ...base, order: node.attrs["order"] as number } : base;
    }
    case "view_block": {
      const a = node.attrs as Record<string, string | null>;
      const cols = node.attrs["columns"];
      const showAll = node.attrs["showAllColumns"];
      const orderDesc = node.attrs["orderDesc"];
      return {
        kind: "view_block",
        viewType: a["viewType"] ?? "list",
        filter: a["filter"] ?? null,
        orderBy: a["orderBy"] ?? null,
        orderDesc: typeof orderDesc === "boolean" ? orderDesc : null,
        groupBy: a["groupBy"] ?? null,
        columns: Array.isArray(cols) ? (cols as string[]) : null,
        showAllColumns: typeof showAll === "boolean" ? showAll : null,
        display: a["display"] ?? "preview-short",
      };
    }
    case "card_embed": {
      const block: Block = { kind: "card_embed", uuid: node.attrs["uuid"] as string };
      if (node.attrs["collapsed"]) block.collapsed = true;
      const height = node.attrs["height"] as number | null;
      if (height != null) block.height = height;
      return block;
    }
    default:
      throw new Error(`Unknown block node: ${node.type.name}`);
  }
}

function listItemsFromPm(list: PmNode): ListItem[] {
  const items: ListItem[] = [];
  list.forEach((item) => {
    const content: Block[] = [];
    item.forEach((child) => content.push(blockFromPm(child)));
    const checked = item.attrs["checked"] as boolean;
    items.push(checked ? { content, checked } : { content });
  });
  return items;
}

function inlineFromPm(node: PmNode): Inline[] {
  const inline: Inline[] = [];
  node.forEach((child) => {
    if (child.type.name === "text") {
      // The link mark carries an href (out-of-band from the formatting marks).
      const linkMark = child.marks.find((m: PmMark) => m.type.name === "link");
      const marks = child.marks
        .filter((m: PmMark) => m.type.name !== "link")
        .map((m: PmMark) => m.type.name as Mark);
      const text = child.text ?? "";
      const node: Inline = { kind: "text", text };
      if (marks.length) node.marks = marks;
      if (linkMark) node.link = linkMark.attrs["href"] as string;
      inline.push(node);
    } else if (child.type.name === "card_link") {
      inline.push({ kind: "card_link", uuid: child.attrs["uuid"] as string });
    } else {
      throw new Error(`Unknown inline node: ${child.type.name}`);
    }
  });
  return inline;
}

// ── Document AST → ProseMirror doc ────────────────────────────────────────────

// Throws when the content does not agree with the schema.
export function parse(content: Document): PmNode {
  const nodes = content.blocks.map(blockToPm);
  const doc = schema.node("doc", null, nodes.length ? nodes : [schema.node("paragraph")]);
  doc.check();
  return doc;
}

function blockToPm(block: Block): PmNode {
  switch (block.kind) {
    case "paragraph":
      return schema.node("paragraph", null, inlineToPm(block.content));
    case "heading":
      return schema.node("heading", { level: block.level }, inlineToPm(block.content));
    case "code_block":
      return schema.node("code_block", null, block.text ? [schema.text(block.text)] : []);
    case "list":
      return schema.node(
        "list",
        { style: block.style, order: block.order ?? 1 },
        block.items.map(listItemToPm)
      );
    case "view_block":
      return schema.node("view_block", {
        viewType: block.viewType,
        filter: block.filter ?? null,
        orderBy: block.orderBy ?? null,
        orderDesc: block.orderDesc ?? null,
        groupBy: block.groupBy ?? null,
        columns: block.columns ?? null,
        showAllColumns: block.showAllColumns ?? null,
        display: block.display,
      });
    case "card_embed":
      return schema.node("card_embed", {
        uuid: block.uuid,
        collapsed: block.collapsed ?? false,
        height: block.height ?? null,
      });
    default:
      throw new Error(`Unknown block: ${(block as { kind: string }).kind}`);
  }
}

function listItemToPm(item: ListItem): PmNode {
  const children = item.content.map(blockToPm);
  return schema.node(
    "list_item",
    { checked: item.checked ?? false },
    children.length ? children : [schema.node("paragraph")]
  );
}

function inlineToPm(content: Inline[]): PmNode[] {
  const nodes: PmNode[] = [];
  for (const item of content) {
    if (item.kind === "text") {
      const marks = (item.marks ?? []).map(markToPm);
      if (item.link) marks.push(schema.marks["link"]!.create({ href: item.link }));
      if (item.text) nodes.push(schema.text(item.text, marks));
    } else if (item.kind === "card_link") {
      nodes.push(schema.node("card_link", { uuid: item.uuid }));
    } else {
      throw new Error(`Unknown inline: ${(item as { kind: string }).kind}`);
    }
  }
  return nodes;
}

function markToPm(mark: Mark): PmMark {
  const type = schema.marks[mark];
  if (!type) throw new Error(`Unknown mark: ${mark}`);
  return type.create();
}
